/**
 * AlertsFilterPanel — Iter82
 *
 * The full filter form. Rendered inside a bottom sheet on mobile and a
 * popover on desktop by `AdminAlertsTab`. All state is CONTROLLED via
 * `draft` + `onChange` — the parent decides whether to apply
 * optimistically or wait for `Appliquer les filtres`.
 *
 * Data-testids for QA (all prefixed `af-` — "alerts filter"):
 *   af-panel                   — root
 *   af-section-<name>          — collapsible section headers
 *   af-time-preset-<id>        — Période chips
 *   af-status-<value>          — Statut chips
 *   af-type-<value>            — Type d'alerte chips
 *   af-asg-<value>             — Affectation chips
 *   af-adv-toggle              — Advanced section header
 *   af-evidence-<presence>     — Présence de preuve (with / without)
 *   af-evkind-<kind>           — Type de preuve chips
 *   af-amendment-<presence>    — Modification citoyen
 *   af-apply / af-reset        — Bottom action buttons
 */
import React, { useState } from "react";
import { ChevronDown, ChevronUp, RotateCcw, Loader2 } from "lucide-react";
import { TimeFilterBar } from "./TimeFilterBar";
import { EMERGENCY_FR } from "../../i18n.jsx";

const STATUS_OPTIONS = [
  { value: "non_dispatched",  label: "Non dispatchée" },
  { value: "dispatched",      label: "Dispatchée" },
  { value: "false",           label: "Fausse alerte" },
  { value: "suspect",         label: "Suspecte" },
  { value: "non_verifiable",  label: "Non vérifiable" },
  { value: "duplicate",       label: "Doublon" },
  { value: "non_verified",    label: "Non vérifiée" },
];

const EVIDENCE_KINDS = [
  { value: "image",    label: "Photo" },
  { value: "video",    label: "Vidéo" },
  { value: "audio",    label: "Audio" },
  { value: "document", label: "Document" },
];

const Section = ({ label, children, defaultOpen = true, testid }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-zinc-800 first:border-t-0" data-testid={`af-section-${testid}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-2.5 text-left"
        aria-expanded={open}
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-300">{label}</span>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
      </button>
      {open && <div className="pb-3 space-y-2">{children}</div>}
    </div>
  );
};

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    aria-pressed={!!active}
    className={`px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] border transition-colors ${
      active
        ? "bg-emerald-500 text-black border-emerald-500"
        : "border-zinc-700 text-zinc-400 hover:text-zinc-100 hover:border-zinc-500"
    }`}
  >
    {children}
  </button>
);

/** Toggle a value in an array (adds if missing, removes if present). */
const toggleArr = (arr, val) => (arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

export const AlertsFilterPanel = ({
  draft,
  onChange,
  onApply,
  onReset,
  agencies = [],
  emergencyTypes = [],
  loading = false,
  disabled = false,
  activeCount = 0,
}) => {
  const patch = (p) => onChange({ ...draft, ...p });

  // Périod toggle: click same preset → clear (§5).
  const setPreset = (id) => {
    if (draft.preset === id && id !== "custom") {
      patch({ preset: null, customSince: "", customUntil: "" });
    } else {
      patch({ preset: id });
    }
  };

  return (
    <div className="p-3 space-y-1" data-testid="af-panel">
      {/* Période */}
      <Section label="Période" testid="periode">
        <TimeFilterBar
          preset={draft.preset || ""}
          customSince={draft.customSince}
          customUntil={draft.customUntil}
          onChange={({ preset: p, customSince: s, customUntil: u }) => {
            if (p === draft.preset && p !== "custom") {
              patch({ preset: null, customSince: "", customUntil: "" });
            } else {
              patch({ preset: p, customSince: s ?? "", customUntil: u ?? "" });
            }
          }}
          testidPrefix="af-time"
        />
        <p className="text-[10px] text-zinc-500 mt-1">
          Astuce&nbsp;: cliquez à nouveau sur la période active pour la retirer.
        </p>
      </Section>

      {/* Statut */}
      <Section label="Statut" testid="statut">
        <div className="flex flex-wrap gap-1.5">
          {STATUS_OPTIONS.map((o) => (
            <Chip
              key={o.value}
              testid={`af-status-${o.value}`}
              active={draft.statuses.includes(o.value)}
              onClick={() => patch({ statuses: toggleArr(draft.statuses, o.value) })}
            >
              {o.label}
            </Chip>
          ))}
        </div>
      </Section>

      {/* Type d'alerte */}
      <Section label="Type d'alerte" testid="type">
        <div className="flex flex-wrap gap-1.5">
          {emergencyTypes.map((t) => {
            const label = EMERGENCY_FR[t]?.label || t;
            return (
              <Chip
                key={t}
                testid={`af-type-${t}`}
                active={draft.types.includes(t)}
                onClick={() => patch({ types: toggleArr(draft.types, t) })}
              >
                {label}
              </Chip>
            );
          })}
        </div>
      </Section>

      {/* Affectation */}
      <Section label="Affectation" testid="affectation">
        <div className="flex flex-wrap gap-1.5">
          <Chip
            testid="af-asg-unassigned"
            active={draft.assignments.includes("unassigned")}
            onClick={() => patch({ assignments: toggleArr(draft.assignments, "unassigned") })}
          >
            Non affectée
          </Chip>
          <Chip
            testid="af-asg-assigned"
            active={draft.assignments.includes("assigned")}
            onClick={() => patch({ assignments: toggleArr(draft.assignments, "assigned") })}
          >
            Affectée à une Agence
          </Chip>
        </div>
        {agencies.length > 0 && (
          <div className="mt-1.5">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-1">
              Ou une agence spécifique
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto cmd-scroll-row">
              {agencies.slice(0, 40).map((a) => {
                const tok = `agency-${a.id}`;
                return (
                  <Chip
                    key={a.id}
                    testid={`af-asg-agency-${a.id}`}
                    active={draft.assignments.includes(tok)}
                    onClick={() => patch({ assignments: toggleArr(draft.assignments, tok) })}
                  >
                    {a.name}
                  </Chip>
                );
              })}
            </div>
          </div>
        )}
      </Section>

      {/* Filtres avancés */}
      <div className="border-t border-zinc-800">
        <button
          type="button"
          data-testid="af-adv-toggle"
          onClick={() => patch({ advOpen: !draft.advOpen })}
          className="w-full flex items-center justify-between py-2.5 text-left"
          aria-expanded={!!draft.advOpen}
        >
          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-300">Filtres avancés</span>
          {draft.advOpen ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
        </button>
        {draft.advOpen && (
          <div className="space-y-3 pb-3">
            {/* Présence de preuve */}
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-1">
                Présence de preuve
              </div>
              <div className="flex gap-1.5">
                <Chip
                  testid="af-evidence-with"
                  active={draft.hasEvidence === true}
                  onClick={() => patch({ hasEvidence: draft.hasEvidence === true ? null : true })}
                >
                  Avec preuve
                </Chip>
                <Chip
                  testid="af-evidence-without"
                  active={draft.hasEvidence === false}
                  onClick={() => patch({ hasEvidence: draft.hasEvidence === false ? null : false })}
                >
                  Sans preuve
                </Chip>
              </div>
            </div>

            {/* Type de preuve */}
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-1">
                Type de preuve
              </div>
              <div className="flex flex-wrap gap-1.5">
                {EVIDENCE_KINDS.map((k) => (
                  <Chip
                    key={k.value}
                    testid={`af-evkind-${k.value}`}
                    active={draft.evidenceKinds.includes(k.value)}
                    onClick={() => patch({ evidenceKinds: toggleArr(draft.evidenceKinds, k.value) })}
                  >
                    {k.label}
                  </Chip>
                ))}
              </div>
            </div>

            {/* Modification citoyen */}
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-1">
                Modification / complément citoyen
              </div>
              <div className="flex gap-1.5">
                <Chip
                  testid="af-amendment-with"
                  active={draft.hasAmendment === true}
                  onClick={() => patch({ hasAmendment: draft.hasAmendment === true ? null : true })}
                >
                  Avec complément
                </Chip>
                <Chip
                  testid="af-amendment-without"
                  active={draft.hasAmendment === false}
                  onClick={() => patch({ hasAmendment: draft.hasAmendment === false ? null : false })}
                >
                  Sans complément
                </Chip>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action row (§15) */}
      <div className="border-t border-zinc-800 pt-3 flex items-center gap-2 flex-wrap sticky bottom-0 bg-[#0f1318]">
        <button
          type="button"
          onClick={onApply}
          disabled={loading || disabled}
          data-testid="af-apply"
          className="flex-1 min-w-[140px] border border-emerald-500/60 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.2em] flex items-center justify-center gap-1.5 disabled:opacity-40"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          Appliquer les filtres{activeCount ? ` (${activeCount})` : ""}
        </button>
        <button
          type="button"
          onClick={onReset}
          data-testid="af-reset"
          className="border border-zinc-700 text-zinc-300 hover:border-rose-500/60 hover:text-rose-300 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.2em] flex items-center gap-1"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Réinitialiser
        </button>
      </div>
    </div>
  );
};

export default AlertsFilterPanel;
